// Guide content loader.
//
// Loads every markdown file under docs/guide at build time via
// Vite's import.meta.glob and parses each entry through the helpers
// in guide-content-helpers.ts. The pure helpers live in a separate
// module so the Node test runner can exercise them without a Vite
// transform — import.meta.glob is unavailable outside Vite.
//
// Markdown body rendering happens at view time via marked in
// Guide.svelte. The Guide content is trusted because every entry is
// gated by the docs/guide CODEOWNERS rule and validated by
// tools/validate-guide.mjs in continuous integration; no user input
// reaches marked.parse.

import {
  parseEntry,
  searchGuide as searchGuideImpl,
  sectionByRoleSet as sectionByRoleSetImpl,
  type GuideEntry,
  type GuideSearchHit,
  type SectionedEntries
} from './guide-content-helpers';

export type {
  GuideCategory,
  GuideFrontmatter,
  GuideEntry,
  GuideSearchHit,
  SectionedEntries
} from './guide-content-helpers';

// Vite resolves this glob at build time. The eager option inlines
// every match as a literal string in the bundle. The path is
// relative to this module; the `..` chain reaches the repository
// root then descends into docs/guide.
const RAW_FILES = import.meta.glob('../../../docs/guide/*.md', {
  query: '?raw',
  import: 'default',
  eager: true
}) as Record<string, string>;

function buildEntries(): GuideEntry[] {
  const entries: GuideEntry[] = [];
  for (const [path, content] of Object.entries(RAW_FILES)) {
    entries.push(parseEntry(path, content));
  }
  entries.sort((a, b) => a.slug.localeCompare(b.slug));
  return entries;
}

const ENTRIES = buildEntries();

export function allGuideEntries(): GuideEntry[] {
  return ENTRIES;
}

export function findGuideEntry(slug: string): GuideEntry | undefined {
  return ENTRIES.find((entry) => entry.slug === slug);
}

export function sectionByRoleSet(roles: readonly string[]): SectionedEntries {
  return sectionByRoleSetImpl(ENTRIES, roles);
}

export function searchGuide(query: string): GuideSearchHit[] {
  return searchGuideImpl(ENTRIES, query);
}
